import React, { useMemo } from 'react';
import { HelpCircle } from 'lucide-react';
import type { ProductFormData } from '@/modelos/productos/Producto';
import Tooltip from '@/contasis/components/Tooltip/Tooltip';
import { Select } from '@/contasis';

export interface TaxOption {
  id: string;
  code: string;
  value: string;
  label: string;
  codigoTributo?: string;
  impuesto?: number;
  nombre?: string;
}

interface ProductPricingSectionProps {
  formData: ProductFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
  taxOptions: TaxOption[];
  onBlur?: () => void;
  onTaxChange?: (option: TaxOption) => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
}

export const ProductPricingSection: React.FC<ProductPricingSectionProps> = ({
  formData,
  setFormData,
  isFieldVisible,
  isFieldRequired,
  taxOptions,
  onBlur,
  onTaxChange,
  showCheck,
  renderCheck,
}) => {
  const { selectedValue, optionsWithLegacy } = useMemo(() => {
    const afectacionId = formData.afectacionTributariaId;
    const trimmedValue = formData.impuesto?.trim();

    if (afectacionId) {
      const matchById = taxOptions.find(option => option.id === afectacionId);
      if (matchById) {
        return { selectedValue: matchById.value, optionsWithLegacy: taxOptions };
      }
    }

    if (trimmedValue) {
      const matchByValue = taxOptions.find(option => option.value === trimmedValue);
      if (matchByValue) {
        return { selectedValue: matchByValue.value, optionsWithLegacy: taxOptions };
      }
    }

    if (!trimmedValue && !afectacionId) {
      return { selectedValue: '', optionsWithLegacy: taxOptions };
    }

    const legacyLabel = trimmedValue || 'Impuesto anterior';
    return {
      selectedValue: legacyLabel,
      optionsWithLegacy: [
        {
          id: 'legacy-impuesto',
          code: 'LEGACY',
          value: legacyLabel,
          label: `${legacyLabel} (no visible)`,
        },
        ...taxOptions,
      ],
    };
  }, [formData.impuesto, formData.afectacionTributariaId, taxOptions]);

  const impuestoVisible = isFieldVisible('impuesto');
  if (!impuestoVisible) {
    return null;
  }

  return (
    <Select
      id="impuesto"
      label="Impuesto"
      required={isFieldRequired('impuesto')}
      labelAction={
        <Tooltip text="Elige el impuesto según el tipo de venta. IGV 18% es el más común." position="right">
          <button
            type="button"
            aria-label="Ayuda: Impuesto"
            className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </Tooltip>
      }
      value={selectedValue}
      onChange={(e) => {
          const val = e.target.value;
          setFormData(prev => ({ ...prev, impuesto: val }));
          const found = optionsWithLegacy.find(o => o.value === val);
          if (found) onTaxChange?.(found);
        }}
      onBlur={onBlur}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      size="small"
    >
      {optionsWithLegacy.map(option => (
        <option key={option.id} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );
};


